import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, FileText, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { formatLocal } from '../lib/dateUtils';

export function EmailDialog({ open, onOpenChange, leadName, form, setForm, onSend }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="email-dialog">
        <DialogHeader><DialogTitle>Send Email to {leadName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="email-subject">Subject *</Label>
            <Input id="email-subject" value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              placeholder="Email subject" data-testid="email-subject-input" />
          </div>
          <div>
            <Label htmlFor="email-body">Message *</Label>
            <Textarea id="email-body" value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Email message" rows={6} data-testid="email-body-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={onSend} data-testid="send-email-button">
              <Mail className="h-4 w-4 mr-2" />Send Email
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function NoteDialog({ open, onOpenChange, leadName, form, setForm, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="note-dialog">
        <DialogHeader><DialogTitle>Add Note for {leadName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="note-text">Note *</Label>
            <Textarea id="note-text" value={form.text}
              onChange={(e) => setForm({ text: e.target.value })}
              placeholder="Enter your note here..." rows={5} data-testid="note-text-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={onSave} data-testid="save-note-button">
              <FileText className="h-4 w-4 mr-2" />Save Note
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CallDialog({ open, onOpenChange, leadName, form, setForm, onSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="call-dialog">
        <DialogHeader><DialogTitle>Log Call with {leadName}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="call-duration">Call Duration (minutes) *</Label>
            <Input id="call-duration" type="tel" inputMode="decimal"
              onInput={(e) => { e.target.value = e.target.value.replace(/[^0-9.]/g, ""); }}
              min="1" value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              placeholder="e.g., 15" data-testid="call-duration-input" />
          </div>
          <div>
            <Label htmlFor="call-notes">Call Notes *</Label>
            <Textarea id="call-notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="What did you discuss?" rows={5} data-testid="call-notes-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={onSave} data-testid="save-call-button">
              <Phone className="h-4 w-4 mr-2" />Log Call
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SummaryDialog({ open, onOpenChange, data }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="summary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            Call Summary — {data?.leadName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[400px] overflow-y-auto">
          {data?.summaries?.map((s, i) => (
            <div key={i} className="border rounded-lg p-3 space-y-2">
              {s.summary && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Summary</p>
                  <p className="text-sm">{s.summary}</p>
                </div>
              )}
              {s.transcript && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Transcript</p>
                  <pre className="text-xs bg-muted p-2 rounded whitespace-pre-wrap max-h-[200px] overflow-y-auto">{s.transcript}</pre>
                </div>
              )}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                {s.duration && <span>{Math.round(s.duration)}s duration</span>}
                {s.created_at && <span>{formatLocal(s.created_at, 'MMM d, h:mm a')}</span>}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
